/**
 * DesignPreview — single-page showcase of the Phase-1 design system
 * primitives. Mounted at `/design-preview` so the admin can see EXACTLY
 * what the new shared components look like before we roll them out
 * across the 18+ existing pages.
 *
 * This file is INTENTIONALLY throwaway: once the rollout is complete
 * we can remove the route or keep it as a Storybook-like reference.
 *
 * Renders:
 *  1. Typography scale  (h1..h3, body, small, mono)
 *  2. KpiCard row       (5 cards with different tones, mimicking the
 *                        customer Transactions header)
 *  3. StatusBadge grid  (all tones × sizes × with/without dot)
 *  4. DataTable demo    (admin Orders-style table with density toggle,
 *                        primary/secondary/numeric cells, hover, etc.)
 *  5. SectionCard demo  (wrapping a small form-ish block)
 */
import React from 'react';
import {
  PageHeader, SectionCard, KpiCard, StatusBadge,
  DataTable, DataTableHead, DataTableBody, DataTH, DataTR, DataTD,
  DataTableEmpty, DensityToggle, useTableDensity,
  toneForStatus, toneForPriority,
} from './ui-shared';
import { Wallet, TrendingUp, ArrowDownRight, ArrowUpRight, Receipt,
  Sparkles, ListChecks, Building2 } from 'lucide-react';

const DEMO_ROWS = [
  { id: 1, num: 'AR-104283', vehicle: 'Car', service: 'DPF/FAP Delete, EGR Removal',  mfr: 'BMW',     model: 'X5',     engine: '3.0d',  ecu: 'EDC17C50', vin: 'WBAFA71060L123456', customer: 'Maxx Tuning',    amount: 220.00, status: 'completed',  priority: 'normal' },
  { id: 2, num: 'AR-104282', vehicle: 'Car', service: 'AdBlue Delete',                  mfr: 'MB',      model: 'Sprinter', engine: '2.2', ecu: 'Bosch MD1', vin: 'WDB9066331S987654', customer: 'Diesel Lab GmbH', amount:  85.00, status: 'in_progress', priority: 'high'   },
  { id: 3, num: 'AR-104281', vehicle: 'Truck', service: 'Stage 1 Tune + DTC Off',       mfr: 'Volvo',   model: 'FH16',   engine: '16.1', ecu: 'EMS2.3',  vin: 'YV2RT40A8DB654321', customer: 'NordicHaul AB',   amount: 450.00, status: 'pending',     priority: 'highest'},
  { id: 4, num: 'AR-104280', vehicle: 'Car',   service: 'SCR Off',                       mfr: 'Audi',    model: 'A4',     engine: '2.0 TDI', ecu: 'EDC17CP20', vin: 'WAUZZZ8K9CA000111', customer: 'Lehmann Tuning',  amount:  60.00, status: 'cancelled',   priority: 'low'    },
  { id: 5, num: 'AR-104279', vehicle: 'Car',   service: 'DTC Removal',                   mfr: 'Ford',    model: 'Focus',  engine: '1.5 TDCi', ecu: 'Delphi DCM6.2', vin: 'WF0BXXMAFGF12345', customer: 'AutoLab UK',     amount:  35.00, status: 'completed',   priority: 'normal' },
];

export default function DesignPreview() {
  const [density, setDensity] = useTableDensity('design-preview-orders', 'default');

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="max-w-7xl mx-auto space-y-10">
        <PageHeader
          icon={<Sparkles className="w-4 h-4" />}
          title="Design refresh — Phase 1 primitives"
          subtitle="Calmer, data-dense aesthetic for the AUTO-REMAP admin & customer portals"
          meta="Inter font · tabular-nums · slate-200 hairlines · outline badges"
          tone="cyan"
        />

        {/* ─────────── 1. Typography scale ─────────── */}
        <SectionCard
          title="Typography scale"
          subtitle="Inter for everything, tabular-nums by default, mono only for IDs/VINs/Orders"
          icon={ListChecks}
        >
          <div className="space-y-3 text-slate-900">
            <div className="text-[28px] font-semibold tracking-tight">Display heading · 28/600</div>
            <div className="text-[22px] font-semibold tracking-tight">Page title · 22/600</div>
            <div className="text-[15px] font-semibold">Section title · 15/600</div>
            <div className="text-sm">Body text · 14/400 — quick brown fox jumps over the lazy dog. €&nbsp;1&nbsp;240.00</div>
            <div className="text-[12px] text-slate-500">Secondary text · 12/400 slate-500</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Eyebrow / Table header · 11/500 uppercase</div>
            <div className="text-[13px] font-mono-ui tabular-nums text-cyan-700">font-mono-ui · AR-104283 · WBAFA71060L123456</div>
          </div>
        </SectionCard>

        {/* ─────────── 2. KpiCard row ─────────── */}
        <div>
          <h3 className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-3">KPI cards</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard tone="neutral" label="Total" value="178"        icon={<Receipt className="w-3.5 h-3.5" />}     hint="All-time" />
            <KpiCard tone="success" label="Paid"  value="€ 12 480"   icon={<Wallet className="w-3.5 h-3.5" />}      hint="EUR" />
            <KpiCard tone="primary" label="Bonus" value="€ 320"      icon={<Sparkles className="w-3.5 h-3.5" />}    hint="Promo credits" />
            <KpiCard tone="success" label="IN"    value="€ 12 800"   icon={<ArrowDownRight className="w-3.5 h-3.5" />} hint="Topped-up + bonuses" />
            <KpiCard tone="danger"  label="OUT"   value="€ 8 940"    icon={<ArrowUpRight className="w-3.5 h-3.5" />}   hint="Spent on orders" />
          </div>
        </div>

        {/* ─────────── 3. Status badge grid ─────────── */}
        <SectionCard title="Status / Priority badges" icon={TrendingUp} iconTone="emerald">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-2">Status</div>
              <div className="flex flex-wrap gap-2">
                <StatusBadge tone="success" dot>Completed</StatusBadge>
                <StatusBadge tone="success">Paid</StatusBadge>
                <StatusBadge tone="warn"    dot>Pending</StatusBadge>
                <StatusBadge tone="warn">Awaiting</StatusBadge>
                <StatusBadge tone="info"    dot>In-progress</StatusBadge>
                <StatusBadge tone="danger"  dot>Cancelled</StatusBadge>
                <StatusBadge tone="danger">Failed</StatusBadge>
                <StatusBadge tone="neutral">Draft</StatusBadge>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-2">Priority</div>
              <div className="flex flex-wrap gap-2">
                <StatusBadge tone={toneForPriority('low')}     dot>Low</StatusBadge>
                <StatusBadge tone={toneForPriority('normal')}>Normal</StatusBadge>
                <StatusBadge tone={toneForPriority('high')}    dot>High</StatusBadge>
                <StatusBadge tone={toneForPriority('highest')} dot>Highest</StatusBadge>
              </div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mt-4 mb-2">Sizes</div>
              <div className="flex items-center gap-2">
                <StatusBadge size="sm" tone="success">sm</StatusBadge>
                <StatusBadge size="md" tone="success">md (default)</StatusBadge>
                <StatusBadge size="lg" tone="success">lg</StatusBadge>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* ─────────── 4. DataTable demo ─────────── */}
        <SectionCard
          title="Orders table"
          subtitle="14-column admin view with density toggle, primary/secondary cells, right-aligned amounts"
          icon={Building2}
          iconTone="cyan"
          actions={<DensityToggle value={density} onChange={setDensity} />}
          bodyClassName="p-0"
        >
          <DataTable density={density} testid="orders-demo-table">
            <DataTableHead>
              <DataTH>Order №</DataTH>
              <DataTH>Vehicle</DataTH>
              <DataTH>Service</DataTH>
              <DataTH>Manufacturer</DataTH>
              <DataTH>Model</DataTH>
              <DataTH>Engine</DataTH>
              <DataTH>ECU</DataTH>
              <DataTH>VIN</DataTH>
              <DataTH>Customer</DataTH>
              <DataTH numeric>Amount</DataTH>
              <DataTH>Status</DataTH>
              <DataTH>Priority</DataTH>
            </DataTableHead>
            <DataTableBody>
              {DEMO_ROWS.map((r) => (
                <DataTR key={r.id} onClick={() => {}}>
                  <DataTD primary>{r.num}</DataTD>
                  <DataTD>{r.vehicle}</DataTD>
                  <DataTD truncate maxWidth={220}>{r.service}</DataTD>
                  <DataTD>{r.mfr}</DataTD>
                  <DataTD>{r.model}</DataTD>
                  <DataTD secondary>{r.engine}</DataTD>
                  <DataTD secondary>{r.ecu}</DataTD>
                  <DataTD secondary><span className="font-mono-ui">{r.vin}</span></DataTD>
                  <DataTD>{r.customer}</DataTD>
                  <DataTD numeric>€&nbsp;{r.amount.toFixed(2)}</DataTD>
                  <DataTD>
                    <StatusBadge tone={toneForStatus(r.status)} dot>
                      {r.status.replace('_', ' ')}
                    </StatusBadge>
                  </DataTD>
                  <DataTD>
                    <StatusBadge tone={toneForPriority(r.priority)}>
                      {r.priority}
                    </StatusBadge>
                  </DataTD>
                </DataTR>
              ))}
            </DataTableBody>
          </DataTable>
        </SectionCard>

        {/* ─────────── 5. Empty state ─────────── */}
        <SectionCard
          title="Empty state pattern"
          subtitle="Used when filters yield no rows or a brand-new account hasn't created any orders yet"
          icon={ListChecks}
          iconTone="slate"
          bodyClassName="p-0"
        >
          <DataTable testid="orders-empty-demo">
            <DataTableHead>
              <DataTH>Order №</DataTH>
              <DataTH>Customer</DataTH>
              <DataTH numeric>Amount</DataTH>
            </DataTableHead>
            <DataTableBody>
              <DataTableEmpty
                colSpan={3}
                icon={<Receipt className="w-5 h-5" />}
                title="No orders yet"
                message="Once you create your first order it will show up here. Use the New Order wizard to get started."
              />
            </DataTableBody>
          </DataTable>
        </SectionCard>
      </div>
    </div>
  );
}
